import { debounceFunc } from '@utils/GeneralUtils';
import ProfileVO, { ProfileType } from '@interfaces/profiles/ProfileVO';
import ProfileContentVO from '@interfaces/contents/ProfileContentVO';
import { updateUsageTracker } from '@service/UsageTrackerService';
import SectionInfoVO from '@interfaces/sections/SectionInfoVO';
import { getAnonymousViewerId } from './ProfileAccessManagementUtils';

interface UsageTrackerProps {
  companyId: string;
  isPublicUser: boolean;
  entityType: string;
  profile: ProfileVO;
  content?: ProfileContentVO;
}

// eslint-disable-next-line import/no-mutable-exports, @typescript-eslint/no-explicit-any -- usage tracker singleton
export let usageTimeTrackVO: { [key: string]: any } | undefined;
let interval: NodeJS.Timeout | null;
let isTabInActive: boolean = false;
let scrollElements: HTMLElement[] = [];
const API_TRIGGER_INTERVAL: number = 1000 * 60 * 5; // 5 minutes interval

let isPublicView: boolean = false;

const initializeUsageTimeTrackVO = (companyId: string, entityType: string, profile: ProfileVO, content?: ProfileContentVO) => {
  usageTimeTrackVO = {
    companyId: profile.companyId,
    profileId: profile.id,
    profileType: profile.type,
    entityType,
    firstActiveTs: new Date()
  };

  if (content?.id) {
    const sectionIdx = profile.sections.findIndex((section: SectionInfoVO) => section.id === content.sectionId);
    usageTimeTrackVO.sectionId = content.sectionId;
    usageTimeTrackVO.sectionName = sectionIdx > -1 ? profile.sections[sectionIdx].name : 'Untitled Section';
    usageTimeTrackVO.contentId = content.id;
    usageTimeTrackVO.contentName = content.name;
  }

  if (profile.type === ProfileType.PUBLIC && isPublicView) {
    usageTimeTrackVO.anonymousViewer = getAnonymousViewerId();
  }
};

const triggerUsageTrackerAPI = () => {
  if (!usageTimeTrackVO || usageTimeTrackVO.onHold) {
    return;
  }

  usageTimeTrackVO.status = undefined;
  updateUsageTracker(
    usageTimeTrackVO,
    () => {},
    () => {},
    isPublicView
  );
  usageTimeTrackVO.firstActiveTs = new Date();
  usageTimeTrackVO.lastActiveTs = new Date();
};

const closeInterval = () => {
  if (!interval) {
    return;
  }
  clearInterval(interval);
  interval = null;
};

const intervalInitializer = () => {
  closeInterval();

  interval = setInterval(() => {
    if (usageTimeTrackVO?.status === 'PENDING' && usageTimeTrackVO.lastActiveTs && !isTabInActive) {
      triggerUsageTrackerAPI();
    } else if (!usageTimeTrackVO?.lastActiveTs || new Date().getTime() - usageTimeTrackVO.lastActiveTs.getTime() > API_TRIGGER_INTERVAL) {
      // Stop the interval if last active timestamp does not exist or more than 5 minutes inactive
      closeInterval();
    } else {
      // Continue the interval
    }
  }, API_TRIGGER_INTERVAL);
};

const reInitiateInterval = () => {
  if (!usageTimeTrackVO) {
    return;
  }

  usageTimeTrackVO.firstActiveTs = new Date();
  usageTimeTrackVO.lastActiveTs = new Date();
  usageTimeTrackVO.status = undefined;
  intervalInitializer();
};

export const trackScrollUsageTime = debounceFunc(() => {
  if (interval && usageTimeTrackVO) {
    usageTimeTrackVO.lastActiveTs = new Date();
    usageTimeTrackVO.status = 'PENDING';
  } else {
    reInitiateInterval();
  }
}, 1000);

const trackClickUsageTime = () => {
  if (!usageTimeTrackVO) {
    return;
  }
  if (interval) {
    usageTimeTrackVO.lastActiveTs = new Date();
    usageTimeTrackVO.status = 'PENDING';
  } else {
    reInitiateInterval();
  }
};

const setupVisibilityChangeListeners = () => {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && isTabInActive) {
      // Reinitialize interval when tab becomes active
      isTabInActive = false;
      reInitiateInterval();
    } else if (!isTabInActive) {
      // Trigger API and close interval when tab becomes inactive
      if (usageTimeTrackVO?.status === 'PENDING' && usageTimeTrackVO.lastActiveTs && !isTabInActive) {
        triggerUsageTrackerAPI();
      }
      closeInterval();
      isTabInActive = true;
    } else {
      // do nothing
    }
  });
};

const cleanupScrollEvents = () => {
  scrollElements.forEach(elem => {
    elem.removeEventListener('scroll', trackScrollUsageTime);
  });
  scrollElements = [];
};

/*
  Initializes usage tracking for a given profile and content. Sets up necessary variables and listeners, and starts the tracking interval.
  Initializes the tracking interval to trigger API calls at a regular interval and checks the activity timestamps.
  Adds event listeners to detect when the browser tab becomes active or inactive, triggering API calls or stopping intervals as necessary.
*/
export const triggerUsageTracker = (props: UsageTrackerProps) => {
  const { companyId, isPublicUser, profile, content, entityType } = props;

  isPublicView = isPublicUser;

  // Initialize the usage tracking object
  initializeUsageTimeTrackVO(companyId, entityType, profile, content);

  // Start the interval for tracking
  intervalInitializer();

  // Set up click listener
  document.addEventListener('click', trackClickUsageTime, true);

  // Set up listeners for visibility change
  setupVisibilityChangeListeners();

  // Return cleanup function to be executed when the component unmounts
  return () => {
    // Clean click event listener
    document.removeEventListener('click', trackClickUsageTime, true);

    // Clean scroll event listener
    cleanupScrollEvents();

    // Clean click event listener
    closeInterval();

    // Make api call to track event when unmount
    if (usageTimeTrackVO?.status === 'PENDING' && usageTimeTrackVO.lastActiveTs) {
      triggerUsageTrackerAPI();
    }
    usageTimeTrackVO = undefined;
  };
};

export const trackUsageTime = (scrollElemId?: string) => {
  if (!usageTimeTrackVO) {
    return;
  }

  if (scrollElemId) {
    const scrollElem = document.getElementById(scrollElemId);
    if (!scrollElem) {
      return;
    }
    // Set up scroll event listener for the specified element
    scrollElem.addEventListener('scroll', trackScrollUsageTime);
    scrollElements.push(scrollElem);
  } else {
    trackClickUsageTime();
  }
};
